import { motion } from "framer-motion";
import { Check, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/context/SiteContentContext";
import { getIcon } from "@/lib/icons";
import { APP_REGISTER_URL } from "@/lib/config";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const FEATURES = [
  { iconKey: "mf1_icon", titleKey: "mf1_title", descKey: "mf1_desc", bulletsKey: "mf1_bullets" },
  { iconKey: "mf2_icon", titleKey: "mf2_title", descKey: "mf2_desc", bulletsKey: "mf2_bullets", linkKey: "mf2_link" },
  { iconKey: "mf3_icon", titleKey: "mf3_title", descKey: "mf3_desc", bulletsKey: "mf3_bullets", linkKey: "mf3_link" },
  { iconKey: "mf4_icon", titleKey: "mf4_title", descKey: "mf4_desc", bulletsKey: "mf4_bullets", linkKey: "mf4_link" },
];

export const MoreFeatures = () => {
  const { t } = useSiteContent();
  return (
    <section id="more" className="relative py-28 px-6" data-testid="more-features-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('more_title')} <span className="text-gradient">{t('more_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">{t('more_subtitle')}</p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => {
            const Icon = getIcon(t(f.iconKey));
            const bullets = (t(f.bulletsKey) || "").split("\n").filter(Boolean);
            return (
              <motion.div
                key={f.titleKey}
                data-testid={`more-feature-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-2xl p-8 hover:border-white/20 transition-colors duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 mb-5">
                  <Icon className="h-6 w-6 text-indigo-300" />
                </span>
                <h3 className="text-xl font-semibold mb-2">{t(f.titleKey)}</h3>
                <p className="text-white/55 leading-relaxed">{t(f.descKey)}</p>
                <ul className="mt-5 flex flex-col gap-2.5">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm text-white/75">
                      <Check className="h-4 w-4 text-emerald-400 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
                {f.linkKey && (
                  <button
                    data-testid={`more-feature-link-${i}`}
                    onClick={() => scrollTo("contact")}
                    className="mt-6 inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
                  >
                    {t(f.linkKey)} →
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Button
            data-testid="more-trial-button"
            onClick={() => { window.location.href = APP_REGISTER_URL; }}
            className="rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white px-8 h-12 gap-2 text-base border-0 shadow-[0_8px_30px_rgba(99,102,241,0.35)]"
          >
            <Zap className="h-5 w-5" /> {t('more_cta')}
          </Button>
        </div>
      </div>
    </section>
  );
};
